import type { ReactElement } from 'react'
import { ContextMenu } from 'radix-ui'
import { ArrowUpRight, Check, ChevronRight, Copy } from 'lucide-react'
import { taskStatuses } from '@/frontend/features/Fde/projectTaskPresentation'
import type { TaskPatch } from '@/frontend/features/Fde/useProjectTaskActions'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'

interface Props {
  task: ProjectTask
  canManage: boolean
  busy: boolean
  onSelect: () => void
  onPatch: (task: ProjectTask, changes: TaskPatch) => void
  onCopy: (task: ProjectTask) => void
  copy: FdeCopy
  children: ReactElement
}
const itemClass =
  'flex cursor-default items-center gap-2 rounded-md px-2.5 py-2 text-xs outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-40'
const menuClass =
  'z-50 min-w-52 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl'

export default function ProjectTaskMenu({
  task,
  canManage,
  busy,
  onSelect,
  onPatch,
  onCopy,
  copy,
  children,
}: Props) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className={menuClass}>
          <ContextMenu.Item className={itemClass} onSelect={onSelect}>
            <ArrowUpRight className="size-3.5 text-muted-foreground" />
            {copy.management.open_task}
          </ContextMenu.Item>
          {canManage && (
            <>
              <ContextMenu.Separator className="my-1 h-px bg-border" />
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger className={itemClass} disabled={busy}>
                  {copy.management.task_status}
                  <ChevronRight className="ml-auto size-3.5" />
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                  <ContextMenu.SubContent className={menuClass}>
                    {taskStatuses.map((status) => (
                      <ContextMenu.Item
                        key={status}
                        className={itemClass}
                        onSelect={() => onPatch(task, { status })}
                      >
                        <span className="size-3.5">
                          {status === task.status && <Check className="size-3.5" />}
                        </span>
                        {copy.management.statuses[status]}
                      </ContextMenu.Item>
                    ))}
                  </ContextMenu.SubContent>
                </ContextMenu.Portal>
              </ContextMenu.Sub>
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger className={itemClass} disabled={busy}>
                  {copy.management.task_priority}
                  <ChevronRight className="ml-auto size-3.5" />
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                  <ContextMenu.SubContent className={menuClass}>
                    {(['low', 'normal', 'high', 'urgent'] as const).map((priority) => (
                      <ContextMenu.Item
                        key={priority}
                        className={itemClass}
                        onSelect={() => onPatch(task, { priority })}
                      >
                        <span className="size-3.5">
                          {priority === task.priority && <Check className="size-3.5" />}
                        </span>
                        {copy.management.priorities[priority]}
                      </ContextMenu.Item>
                    ))}
                  </ContextMenu.SubContent>
                </ContextMenu.Portal>
              </ContextMenu.Sub>
            </>
          )}
          <ContextMenu.Separator className="my-1 h-px bg-border" />
          <ContextMenu.Item className={itemClass} onSelect={() => onCopy(task)}>
            <Copy className="size-3.5 text-muted-foreground" />
            {copy.management.copy_task_link}
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}
